import {
    Button,
    Drawer,
    Typography,
    Avatar,
    Space,
    Tag,
    theme,
    Empty,
    Tooltip,
    Card,
    Divider,
    Spin,
} from 'antd'
import React, { useEffect, useState, useRef } from 'react'
import { candidateAddNote, candidateNotesList } from '../store/candidate.slice'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import { useDispatch, useSelector } from 'react-redux'
import { CANDIDATE_STATUS } from './candidate-status-tab-list'
import AttachmentUploader from './attachment-uploader-note'
import { uploadResume, uploadResumeS3Url } from '../service/candidate.service'
import AttachmentPreview from '../../../components/attachmentPreview/attachmentPreview'
import { BinIcon } from '../../../assets/icons'
import DeletePopUp from '../../../components/deletePopUp/deletePopUp'
import { camelCaseToWordConverter } from '../../../utils/commonUtils'
import ProfileAvatar from './profil-avatar'

const { Text } = Typography
const initialState = {
    addCommentLoader: false,
    comment: '',
    deleteModal: false,
    deleteButtonLoading: false,
}

const NoteDrawer = ({ visible, onClose, candidate }) => {
    const [attachments, setAttachments] = useState([])
    const [noteInput, setNoteInput] = useState('')
    const [activeDate, setActiveDate] = useState('')
    const [state, setState] = useState(initialState)
    const [isSaving, setIsSaving] = useState(false)
    const { useToken } = theme
    const convertToUserStatus = 'convertToUser'

    const { token } = useToken()
    const dispatch = useDispatch()

    const containerRef = useRef()
    const dateRefs = useRef({})

    const { userProfile } = useSelector((state) => state.loginSlice)
    const { candidateNotes, isLoading } = useSelector((state) => state.candidateSlice)
    const status = CANDIDATE_STATUS.find((s) => s?.value == candidate?.status)
    const getStatusExplanation = (statusValue) => {
        const status = CANDIDATE_STATUS.find((s) => s.label == statusValue)
        return status?.tooltip
    }

    const commentStatus = (status) => {
        const color = CANDIDATE_STATUS.find((s) => s?.value == status)
        return color?.color
    }

    const handleSaveNoteWithAttachments = async (
        candidateId,
        fieldName = 'add'
    ) => {
        setIsSaving(true) // start loading
        try {
            let uploadedLinks = null

            if (attachments.length) {
                const payload = {
                    data: attachments.map((f) => ({
                        filename: f.name,
                        contentType: f.type,
                    })),
                }

                const { data } = await uploadResume(payload)

                if (data.success) {
                    uploadedLinks = []

                    for (const file of attachments) {
                        const match = data.data.find(
                            (item) => item.filename === file.name
                        )
                        if (!match) {
                            console.error(
                                `No upload URL for file: ${file.name}`
                            )
                            continue
                        }

                        const response = await uploadResumeS3Url(
                            file,
                            match.uploadUrl,
                            match.publicLink,
                            match.name
                        )
                        if (response.ok) {
                            uploadedLinks.push({
                                title: file.name,
                                displayName: file.name,
                                category: 'document',
                                file: {
                                    fileName: file.name,
                                    fileLink: match.publicLink,
                                    fileType: file.type,
                                    uploadDate: Date.now(),
                                },
                            })
                        } else {
                            console.error(
                                `Failed to upload ${file.name}:`,
                                await response.text()
                            )
                        }
                    }

                    if (uploadedLinks.length === 0) uploadedLinks = null
                }
            }

            await handleAddNote(candidateId, uploadedLinks, fieldName)
        } catch (error) {
            console.error('Error saving note with attachments:', error)
        } finally {
            setIsSaving(false) // stop loading
        }
    }

    const handleAddNote = async (
        candidateId,
        attachments = null,
        fieldName = 'add'
    ) => {
        if (!noteInput.trim()) return

        try {
            const payload = {
                candidateComment: {
                    content: noteInput,
                    createdDate: Date.now(),
                    status: candidate.status,
                    createdBy: userProfile._id,
                    attachments: attachments,
                },
                fieldName,
            }

            const { data } = await candidateAddNote(candidateId, payload)

            if (data.success) {
                setNoteInput('')
                handleGetCandidateNotes(candidateId)
                setAttachments([])
            }
        } catch (error) {
            console.error('Error adding note:', error)
        }
    }

    const handleRemoveNotes = async (candidateId, fieldName = 'remove') => {
        try {
            const payload = {
                candidateCommentId: state?.comment?._id,
                fieldName,
            }

            const { data } = await candidateAddNote(candidateId, payload)
            if (data.success) {
                handleGetCandidateNotes(candidateId)
                setState((prevState) => ({
                    ...prevState,
                    addCommentLoader: false,
                    deleteButtonLoading: false,
                    deleteModal: false,
                }))
            }
        } catch (error) {
            console.log('🚀 ~ handleRemoveNotes ~ error:', error)
        }
    }

    const isMeaningfulContent = (html) => {
        if (!html) return false

        const temp = document.createElement('div')
        temp.innerHTML = html

        // Remove <br> and check for real text
        return temp.textContent.trim().length > 0
    }

    const handleOnChange = (value) => {
        setNoteInput(value)
    }

    const handleGetCandidateNotes = (candidateId) => {
        try {
            dispatch(candidateNotesList({ candidateId }))
        } catch (error) {
            console.error('Error fetching notes:', error)
        }
    }

    const groupNotesByDate = (notes) => {
        return notes.reduce((acc, note) => {
            const date = new Date(note.createdDate)
            const dayKey = date.toDateString() // e.g., "Fri May 09 2025"
            if (!acc[dayKey]) acc[dayKey] = []
            acc[dayKey].push(note)
            return acc
        }, {})
    }

    const formatDateForHeader = (dateString) => {
        const today = new Date()
        const inputDate = new Date(dateString)
        const yesterday = new Date()
        yesterday.setDate(today.getDate() - 1)

        if (inputDate.toDateString() === today.toDateString()) return 'Today'
        if (inputDate.toDateString() === yesterday.toDateString())
            return 'Yesterday'

        return inputDate.toLocaleDateString('en-IN', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        })
    }

    const groupedNotes = groupNotesByDate(candidateNotes || [])

    useEffect(() => {
        if (candidate?._id) {
            handleGetCandidateNotes(candidate._id)
        }
    }, [candidate])

    useEffect(() => {
        const container = containerRef.current
        if (!container) return

        const handleScroll = () => {
            const scrollTop = container.scrollTop
            const dateKeys = Object.keys(dateRefs.current)

            for (let i = dateKeys.length - 1; i >= 0; i--) {
                const key = dateKeys[i]
                const ref = dateRefs.current[key]?.current
                if (
                    ref &&
                    ref.offsetTop - container.offsetTop <= scrollTop + 10
                ) {
                    setActiveDate(formatDateForHeader(key))
                    break
                }
            }
        }

        container.addEventListener('scroll', handleScroll)
        return () => container.removeEventListener('scroll', handleScroll)
    }, [])

    useEffect(() => {
        if (containerRef.current) {
            containerRef.current.scrollTop = containerRef.current.scrollHeight
        }
    }, [candidateNotes])


    const handleColseDrawer = () => {
        onClose(false)
        setNoteInput("")
    }
    return (
        <>
            <Drawer
                title={
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            {/* <Avatar size="large">
                                {candidate?.fullName?.charAt(0) || 'A'}
                            </Avatar> */}
                            <ProfileAvatar
                                profilePicture={candidate?.profilePicture}
                                name={candidate?.fullName}
                            />
                            <div style={{ marginLeft: '10px' }}>
                                <Text strong>
                                    {candidate?.fullName || 'Name'}
                                </Text>
                                <br />
                                <Text>
                                    {candidate?.personalEmail || 'Email'}
                                </Text>
                            </div>
                        </div>
                        <Tag color={status?.color} style={{ cursor: 'auto' }}>
                            {camelCaseToWordConverter(candidate?.status)}
                        </Tag>
                    </div>
                }
                placement="right"
                width={650}
                onClose={() => handleColseDrawer()}
                closeIcon={false}
                visible={visible}
                bodyStyle={{
                    padding: '14px 24px',
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100vh',
                }}
                styles={{
                    body: {
                        padding: '14px 24px',
                    },
                    content: {
                        height: '100vh',
                    },
                    wrapper: {
                        minHeight: '100vh',
                    },
                }}
            >
                {/* Sticky Date Header */}
                {/* {activeDate && (
                <div style={{
                    position: 'sticky',
                    top: 0,
                    background: '#fff',
                    zIndex: 100,
                    padding: '4px 0',
                    textAlign: 'center',
                    borderBottom: '1px solid #f0f0f0'
                }}>
                    <Tag color="blue">{activeDate}</Tag>
                </div>
            )} */}

                {/* Scrollable Notes List */}
                <div
                    ref={containerRef}
                    style={{ flex: 1, overflowY: 'auto', marginBottom: '12px' }}
                >
                    <Space
                        direction="vertical"
                        style={{
                            width: '100%',
                            height:
                                candidateNotes?.length === 0 && '100%',
                            justifyContent: 'center',
                        }}
                    >
                        <Spin spinning={isLoading}>
                            {candidateNotes?.length === 0 ? (
                                <div
                                    style={{
                                        textAlign: 'center',
                                        padding: '40px 20px',
                                        color: '#999',
                                        fontSize: 16,
                                        // height: "100%"
                                    }}
                                >
                                    <Empty description={' No Notes found.'} />
                                </div>
                            ) : (



                                Object.entries(groupedNotes).map(
                                    ([dateKey, notes]) => {
                                        const readableDate =
                                            formatDateForHeader(dateKey)
                                        if (!dateRefs.current[dateKey]) {
                                            dateRefs.current[dateKey] =
                                                React.createRef()
                                        }
                                        return (
                                            <div
                                                key={dateKey}
                                                ref={dateRefs.current[dateKey]}
                                            >
                                                <div
                                                    style={{
                                                        textAlign: 'center',
                                                        margin: '16px 0',
                                                    }}
                                                >
                                                    <Tag
                                                        style={{
                                                            zIndex:
                                                                activeDate ==
                                                                readableDate &&
                                                                100,
                                                            position:
                                                                activeDate ==
                                                                readableDate &&
                                                                'sticky',
                                                            cursor: 'auto',
                                                        }}
                                                    >
                                                        {readableDate}
                                                    </Tag>
                                                </div>

                                                {notes.map((msg, index) => {
                                                    const time = new Date(
                                                        msg.createdDate
                                                    ).toLocaleTimeString('en-IN', {
                                                        hour: '2-digit',
                                                        minute: '2-digit',
                                                    })

                                                    const userName =
                                                        msg?.createdByUser?.name ||
                                                        'Unknown'
                                                    const status =
                                                        camelCaseToWordConverter(
                                                            msg?.status
                                                        )
                                                    const statusColor =
                                                        commentStatus(msg?.status)

                                                    return (
                                                        <Card
                                                            key={msg._id || index}
                                                            style={{
                                                                marginBottom: 12,
                                                                borderRadius: 12,
                                                            }}
                                                            bodyStyle={{
                                                                padding: 16,
                                                            }}
                                                        >
                                                            <Space
                                                                style={{
                                                                    justifyContent:
                                                                        'space-between',
                                                                    width: '100%',
                                                                }}
                                                            >
                                                                <Space align="center">
                                                                    <Avatar
                                                                        style={{
                                                                            backgroundColor:
                                                                                'rgb(250, 250, 250)',
                                                                            color: '#000000e0',
                                                                            border: '1px solid rgb(204, 204, 204)',
                                                                            fontSize: 12,
                                                                        }}
                                                                        size={30}
                                                                    >
                                                                        {userName?.[0]?.toUpperCase()}
                                                                    </Avatar>
                                                                    <div>
                                                                        <Text
                                                                            strong
                                                                        >
                                                                            {
                                                                                userName
                                                                            }
                                                                        </Text>
                                                                        <br />
                                                                        <Text
                                                                            type="secondary"
                                                                            style={{
                                                                                fontSize: 11,
                                                                            }}
                                                                        >
                                                                            {time}
                                                                        </Text>
                                                                    </div>
                                                                </Space>
                                                                <Space>
                                                                    <Tooltip
                                                                        title={getStatusExplanation(
                                                                            status
                                                                        )}
                                                                    >
                                                                        <Tag
                                                                            color={
                                                                                statusColor
                                                                            }
                                                                            style={{
                                                                                fontSize: 11,
                                                                                cursor: 'auto',
                                                                            }}
                                                                        >
                                                                            {status}
                                                                        </Tag>
                                                                    </Tooltip>
                                                                    {candidate?.status !=
                                                                        convertToUserStatus && (
                                                                            <Tooltip
                                                                                Tooltip
                                                                                title="Delete"
                                                                            >
                                                                                <div
                                                                                    style={{
                                                                                        height: '20px',
                                                                                        width: '20px',
                                                                                        cursor: 'pointer',
                                                                                    }}
                                                                                    onClick={() => {
                                                                                        setState(
                                                                                            (
                                                                                                prevState
                                                                                            ) => ({
                                                                                                ...prevState,
                                                                                                deleteModal: true,
                                                                                                comment:
                                                                                                    msg,
                                                                                            })
                                                                                        )
                                                                                    }}
                                                                                >
                                                                                    <BinIcon
                                                                                        color={
                                                                                            token
                                                                                                .colorPalette
                                                                                                .baseColor
                                                                                                .error
                                                                                        }
                                                                                    />
                                                                                </div>
                                                                            </Tooltip>
                                                                        )}
                                                                </Space>
                                                            </Space>

                                                            {isMeaningfulContent(
                                                                msg.content
                                                            ) && (
                                                                    <div
                                                                        style={{
                                                                            marginTop: 12,
                                                                        }}
                                                                        dangerouslySetInnerHTML={{
                                                                            __html: msg.content,
                                                                        }}
                                                                    />
                                                                )}

                                                            {msg?.attachments
                                                                ?.length > 0 && (
                                                                    <>
                                                                        <Divider
                                                                            style={{
                                                                                margin: '12px 0',
                                                                            }}
                                                                        />
                                                                        <Space wrap>
                                                                            {msg.attachments.map(
                                                                                (
                                                                                    fileObj,
                                                                                    i
                                                                                ) => (
                                                                                    <AttachmentPreview
                                                                                        key={
                                                                                            fileObj._id ||
                                                                                            i
                                                                                        }
                                                                                        file={
                                                                                            fileObj
                                                                                        }
                                                                                    />
                                                                                )
                                                                            )}
                                                                        </Space>
                                                                    </>
                                                                )}
                                                        </Card>
                                                    )
                                                })}
                                            </div>
                                        )
                                    }
                                )

                            )}
                        </Spin>
                    </Space>
                </div>

                {/* Input & Save Button */}
                {candidate?.status != convertToUserStatus && (
                    <div
                        style={{
                            borderTop: '1px solid #f0f0f0',
                            paddingTop: '10px',
                            height: "300px"
                        }}
                    >
                        <div
                            style={{
                                marginTop: '15px',
                                marginBottom: '15px',
                                display: 'flex',
                                alignItems: 'flex-start',
                            }}
                        >
                            <AttachmentUploader
                                attachments={attachments}
                                setAttachments={setAttachments}
                            />
                        </div>
                        <ReactQuill
                            theme="snow"
                            style={{
                                backgroundColor: '#f8f8f8',
                                borderRadius: '12px',
                                border: 'none',
                                margin: '10px 0px',
                                fontFamily: "'Nunito Sans', sans-serif",
                                height: "135px"
                            }}
                            onChange={handleOnChange}
                            value={noteInput}
                            placeholder={'Write a note...'}
                        />

                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                justifyContent: 'end',
                                marginTop: "48px"
                            }}
                        >
                            <Button
                                disabled={!noteInput || !noteInput.replace(/<[^>]*>/g, '').trim()}
                                // type="primary"
                                onClick={() =>
                                    handleSaveNoteWithAttachments(candidate._id)
                                }
                                loading={isSaving}
                            >
                                Save Note
                            </Button>
                        </div>
                    </div>
                )}
            </Drawer>
            <DeletePopUp
                previewDelete={state?.deleteModal}
                onDelete={() => {
                    setState((prevState) => ({
                        ...prevState,
                        deleteButtonLoading: true,
                    }))
                    handleRemoveNotes(candidate._id, 'remove')
                }}
                loadingButton={state?.deleteButtonLoading}
                onCancel={() => {
                    setState((prevState) => ({
                        ...prevState,
                        deleteButtonLoading: false,
                        deleteModal: false,
                    }))
                }}
                modalTitle="Delete Comment"
                pageKey="Comment"
            />
        </>
    )
}

export default NoteDrawer
